import React, {FC, useCallback, useEffect, useMemo} from "react";
import {useAxios} from "../../utils/hooks";
import useSafeState from "../../utils/safe-state";
import {Trans, t} from "@lingui/macro";
import {useLingui} from "@lingui/react";
import {
    Badge,
    Breadcrumb,
    Button,
    Card, Checkbox, DatePicker, Descriptions,
    Divider,
    Form,
    Input,
    List,
    message,
    Popconfirm,
    Radio,
    Select,
    Space, Spin, Tag,
    Upload
} from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../components/dto-table/dto-table";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import MscStockModel from "../../models/mscstock/msc-stock-model";
import {openNotification} from "../../utils/openNotification";
import { useHistory } from "react-router-dom";
import {
    CodeSandboxOutlined,
    DeleteOutlined, DownloadOutlined,
    ExclamationCircleOutlined,
    FileSyncOutlined,
    FolderOpenOutlined, SketchOutlined, TeamOutlined
} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import {AxiosResponse} from "axios";
import FormItem from "antd/es/form/FormItem";
import {useForm} from "antd/es/form/Form";
import moment from "moment/moment";

type SupplierOption = {
    code: string;
    name?: string;
    isEu?: boolean | null;
};


const MyBusinessProfileScreen: FC = () => {
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const {i18n} = useLingui();
    const [loading, setLoading] = useSafeState(false);
    const history = useHistory();
    const [form] = Form.useForm();
    const [dataLoading, setDataLoading] = useSafeState(false);
    const [supplierOptions, setSupplierOptions] = useSafeState<SupplierOption[]>([]);
    const [supplierLoading, setSupplierLoading] = useSafeState(false);

    const initialSupplierCode = useMemo(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const stored = localStorage.getItem('supplierCode');
        if (!stored || stored === 'undefined' || stored === 'null' || stored.trim() === '') {
            return undefined;
        }
        return stored;
    }, []);

    const requiresSupplierSelection = !initialSupplierCode;
    const [selectedSupplier, setSelectedSupplier] = useSafeState<string | undefined>(initialSupplierCode);

    const getAllData = useCallback((supplierCodeParam?: string) => {

        if (axiosInstance.current == null) {
            return;
        }

        setDataLoading(true);
        axiosInstance.current
            .get('/resource/onboarding-data/get-all-onboarding-data', {
                params: supplierCodeParam ? {supplierCode: supplierCodeParam} : undefined
            })
            .then((response: AxiosResponse<any>) => {
                const data = response.data || {};

                data.profile = data.profile || {};
                data.finishedProduct = data.finishedProduct || {};
                data.rawMaterial = data.rawMaterial || {};
                data.indigenous = data.indigenous || {};
                data.anyConcerns = data.anyConcerns || {};

                // Process profile data
                if (data.profile.sme !== undefined && data.profile.sme !== null) {
                    data.profile.sme = Number(data.profile.sme);
                }
                if (data.profile.collectInfo !== undefined && data.profile.collectInfo !== null) {
                    data.profile.collectInfo = Number(data.profile.collectInfo);
                }
                if (data.profile.collectInfoDeadline) {
                    data.profile.collectInfoDeadline = moment(data.profile.collectInfoDeadline);
                }
                if (data.profile.riskAssessment !== undefined && data.profile.riskAssessment !== null) {
                    data.profile.riskAssessment = Number(data.profile.riskAssessment);
                }
                if (data.profile.riskAssessmentDeadline) {
                    data.profile.riskAssessmentDeadline = moment(data.profile.riskAssessmentDeadline);
                }
                if (data.profile.goodsMixingCounterMeasureDeadline) {
                    data.profile.goodsMixingCounterMeasureDeadline = moment(data.profile.goodsMixingCounterMeasureDeadline);
                }
                if (data.profile.rawMatGoodsMixingCounterMeasureDeadline) {
                    data.profile.rawMatGoodsMixingCounterMeasureDeadline = moment(data.profile.rawMatGoodsMixingCounterMeasureDeadline);
                }
                if (data.profile.helpfulForIdentifyingCircumventionRisks !== undefined && data.profile.helpfulForIdentifyingCircumventionRisks !== null) {
                    data.profile.helpfulForIdentifyingCircumventionRisks = Number(data.profile.helpfulForIdentifyingCircumventionRisks);
                }
                if (data.profile.haveAnyConcerns !== undefined && data.profile.haveAnyConcerns !== null) {
                    data.profile.haveAnyConcerns = Number(data.profile.haveAnyConcerns);
                }
                if (data.profile.haveAnyEarlyStageInvestigation !== undefined && data.profile.haveAnyEarlyStageInvestigation !== null) {
                    data.profile.haveAnyEarlyStageInvestigation = Number(data.profile.haveAnyEarlyStageInvestigation);
                }
                if (data.profile.checkedSyntheticRubber) {
                    data.profile.checkedSyntheticRubber = data.profile.checkedSyntheticRubber === "true";
                }
                if (data.profile.checkedLegacyStock) {
                    data.profile.checkedLegacyStock = data.profile.checkedLegacyStock === "true";
                }

                if (data.finishedProduct.goodsMixing !== undefined && data.finishedProduct.goodsMixing !== null) {
                    data.finishedProduct.goodsMixing = Number(data.finishedProduct.goodsMixing);
                }
                if (data.finishedProduct.countermeasureDeadline) {
                    data.finishedProduct.countermeasureDeadline = moment(data.finishedProduct.countermeasureDeadline);
                }
                //==================================
                if (data.rawMaterial.goodsMixing !== undefined && data.rawMaterial.goodsMixing !== null) {
                    data.rawMaterial.goodsMixing = Number(data.rawMaterial.goodsMixing);
                }
                if (data.rawMaterial.chainTransparency !== undefined && data.rawMaterial.chainTransparency !== null) {
                    data.rawMaterial.chainTransparency = Number(data.rawMaterial.chainTransparency);
                }
                if (data.rawMaterial.helpfulForIdentifyingCircumventionRisks !== undefined && data.rawMaterial.helpfulForIdentifyingCircumventionRisks !== null) {
                    data.rawMaterial.helpfulForIdentifyingCircumventionRisks = Number(data.rawMaterial.helpfulForIdentifyingCircumventionRisks);
                }
                if (data.rawMaterial.intermediaries !== undefined && data.rawMaterial.intermediaries !== null) {
                    data.rawMaterial.intermediaries = Number(data.rawMaterial.intermediaries);
                }
                if (data.rawMaterial.originIsClear !== undefined && data.rawMaterial.originIsClear !== null) {
                    data.rawMaterial.originIsClear = Number(data.rawMaterial.originIsClear);
                }
                if (data.rawMaterial.countermeasureDeadline) {
                    data.rawMaterial.countermeasureDeadline = moment(data.rawMaterial.countermeasureDeadline);
                }
                if (data.rawMaterial.checkedTraseEarth) {
                    data.rawMaterial.checkedTraseEarth = data.rawMaterial.checkedTraseEarth === "true";
                }
                if (data.rawMaterial.containsWoodPulp !== undefined && data.rawMaterial.containsWoodPulp !== null) {
                    data.rawMaterial.containsWoodPulp = Number(data.rawMaterial.containsWoodPulp);
                }
                //==================================
                if (data.indigenous.presence !== undefined && data.indigenous.presence !== null) {
                    data.indigenous.presence = Number(data.indigenous.presence);
                }

                if (data.indigenous.consultedWithNGO !== undefined && data.indigenous.consultedWithNGO !== null) {
                    data.indigenous.consultedWithNGO = Number(data.indigenous.consultedWithNGO);
                }

                if (data.indigenous.consultedWithLocalIndigenousConcils !== undefined && data.indigenous.consultedWithLocalIndigenousConcils !== null) {
                    data.indigenous.consultedWithLocalIndigenousConcils = Number(data.indigenous.consultedWithLocalIndigenousConcils);
                }

                if (data.indigenous.hasFPIC !== undefined && data.indigenous.hasFPIC !== null) {
                    data.indigenous.hasFPIC = Number(data.indigenous.hasFPIC);
                }

                if (data.indigenous.consultationWithIndigenousCommunities !== undefined && data.indigenous.consultationWithIndigenousCommunities !== null) {
                    data.indigenous.consultationWithIndigenousCommunities = Number(data.indigenous.consultationWithIndigenousCommunities);
                }

                if (data.indigenous.isRatifiedTheILO169Convention !== undefined && data.indigenous.isRatifiedTheILO169Convention !== null) {
                    data.indigenous.isRatifiedTheILO169Convention = Number(data.indigenous.isRatifiedTheILO169Convention);
                }

                if (data.indigenous.anyPublicStatementsOrRecordsOfNonConsensualLandUse !== undefined && data.indigenous.anyPublicStatementsOrRecordsOfNonConsensualLandUse !== null) {
                    data.indigenous.anyPublicStatementsOrRecordsOfNonConsensualLandUse = Number(data.indigenous.anyPublicStatementsOrRecordsOfNonConsensualLandUse);
                }

                    if (data.indigenous.hasAnyLegalAndDocumentedClaimToTheLand !== undefined && data.indigenous.hasAnyLegalAndDocumentedClaimToTheLand !== null) {
                        data.indigenous.hasAnyLegalAndDocumentedClaimToTheLand = Number(data.indigenous.hasAnyLegalAndDocumentedClaimToTheLand);
                    }

                    if (data.indigenous.dataFromNationalOrLocalLandRegistryDeadline) {
                        data.indigenous.dataFromNationalOrLocalLandRegistryDeadline = moment(data.indigenous.dataFromNationalOrLocalLandRegistryDeadline);
                    }

                if (data.indigenous.checkedCulturalSurvival) {
                    data.indigenous.checkedCulturalSurvival = data.indigenous.checkedCulturalSurvival === "true";
                }
                if (data.indigenous.checkedForestPeoples1) {
                    data.indigenous.checkedForestPeoples1 = data.indigenous.checkedForestPeoples1 === "true";
                }
                if (data.indigenous.checkedForestPeoples2) {
                    data.indigenous.checkedForestPeoples2 = data.indigenous.checkedForestPeoples2 === "true";
                }
                if (data.indigenous.checkedNormlexIlo) {
                    data.indigenous.checkedNormlexIlo = data.indigenous.checkedNormlexIlo === "true";
                }
                //==================================

                if (data.anyConcerns.haveAnyConcerns !== undefined && data.anyConcerns.haveAnyConcerns !== null) {
                    data.anyConcerns.haveAnyConcerns = Number(data.anyConcerns.haveAnyConcerns);
                }
                if (data.anyConcerns.haveAnyEarlyStageInvestigation !== undefined && data.anyConcerns.haveAnyEarlyStageInvestigation !== null) {
                    data.anyConcerns.haveAnyEarlyStageInvestigation = Number(data.anyConcerns.haveAnyEarlyStageInvestigation);
                }
                if (data.anyConcerns.isThereLiveSanction !== undefined && data.anyConcerns.isThereLiveSanction !== null) {
                    data.anyConcerns.isThereLiveSanction = Number(data.anyConcerns.isThereLiveSanction);
                }

                form.resetFields();
                form.setFieldsValue({
                    onboarding: data
                });
            })
            .catch(error => {
                openNotification('error', error);
            })
            .finally(() => {
                setDataLoading(false);
            });
    }, [axiosInstance, form, setDataLoading]);

    const fetchSuppliers = useCallback(() => {
        if (axiosInstance.current == null) {
            return;
        }

        setSupplierLoading(true);
        axiosInstance.current
            .get('/resource/suppliers')
            .then(response => {
                const suppliers = Array.isArray(response.data) ? response.data : [];
                setSupplierOptions(suppliers);
            })
            .catch(error => {
                openNotification('error', error);
            })
            .finally(() => {
                setSupplierLoading(false);
            });
    }, [axiosInstance, setSupplierLoading, setSupplierOptions]);

    useEffect(() => {
        if (!requiresSupplierSelection) {
            return;
        }

        if (supplierOptions.length > 0) {
            return;
        }

        fetchSuppliers();
    }, [requiresSupplierSelection, fetchSuppliers, supplierOptions.length]);

    useEffect(() => {
        if (requiresSupplierSelection && !selectedSupplier) {
            form.resetFields();
            return;
        }

        if (requiresSupplierSelection) {
            getAllData(selectedSupplier);
        } else {
            getAllData(initialSupplierCode);
        }
    }, [requiresSupplierSelection, selectedSupplier, getAllData, form, initialSupplierCode]);

    const reset = () => {

        if (axiosInstance.current != null) {
            setLoading(true);
            const config = requiresSupplierSelection && selectedSupplier 
                ? {params: {supplierCode: selectedSupplier}} 
                : {};
            
            axiosInstance.current
                .put('/resource/onboarding/reset-onboarding', {}, config)
                .then(() => {
                    setLoading(false);
                    window.location.href = "/onboardingfinish";
                })
                .catch(function (error) {
                    openNotification('error', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleDownloadFile = (filePath: any, fileName: any): void => {
        if (axiosInstance.current != null) {
            const FileDownload = require('js-file-download');
            axiosInstance
                .current({
                    url: process.env.REACT_APP_API_BASE_URL + '/public/file/get?folder=' + 'eudrdocuments' + '&filename=' + filePath,
                    method: 'GET',
                    responseType: 'blob',
                })
                .then(response => {
                    FileDownload(response.data, fileName);
                });
        }
    };

    return (<>
            <div id="alma" style={{ overflowY: "auto"}}>
                <Breadcrumb style={{marginBottom: 10}}>
                    <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'My Business Profile'}></Trans></Breadcrumb.Item>
                </Breadcrumb>
                <Spin spinning={dataLoading}>
                    <Form form={form}>
                        {requiresSupplierSelection && (
                            <Form.Item
                                label={<Trans>Select supplier</Trans>}
                                colon={false}
                                style={{maxWidth: 360}}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder={i18n._(t`Select supplier to view data`)}
                                    optionFilterProp="children"
                                    value={selectedSupplier}
                                    onChange={(value) => setSelectedSupplier(typeof value === 'string' ? value : undefined)}
                                    loading={supplierLoading}
                                >
                                    {supplierOptions.map(supplier => (
                                        <Select.Option key={supplier.code} value={supplier.code}>
                                            {supplier.code}{supplier.name ? ` - ${supplier.name}` : ''}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                <Descriptions style={{marginTop: 10}} bordered>
                    <Descriptions.Item span={4} label={<Trans>1. Does your organisation qualified as an SME?</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item name={["onboarding", "profile", "sme"]} style={{marginBottom: 0}}>
                            <Radio.Group disabled={true}>
                                <Space direction="horizontal">
                                    <Radio value={1}><Trans>Yes</Trans></Radio>
                                    <Radio value={2}><Trans>No</Trans></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<><Trans>2. Did you collect necessary information from your suppliers in line with EUDR (EU) 2023/1115?</Trans><br/><Trans>If NO, please indicate target date to collect necessary information.</Trans></>} labelStyle={{width: '50%'}}>
                        <Form.Item name={["onboarding", "profile", "collectInfo"]} style={{marginBottom: 10}}>
                            <Radio.Group disabled={true}>
                                <Space direction="horizontal">
                                    <Radio value={1}><Trans>Yes</Trans></Radio>
                                    <Radio value={2}><Trans>No</Trans></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item name={["onboarding", "profile", "collectInfoDeadline"]} style={{marginBottom: 0}}>
                            <DatePicker disabled={true}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<><Trans>3. Did you perform risk assessment in line with EUDR (EU) 2023/1115?</Trans><br/><Trans>What was the result of the risk assessment?</Trans><br/><Trans>If you did not perform yet, plase indicate target date for completing your risk assessment.</Trans></>} labelStyle={{width: '50%'}}>
                        <Form.Item name={["onboarding", "profile", "riskAssessment"]} style={{marginBottom: 10}}>
                            <Radio.Group disabled={true}>
                                <Space direction="horizontal">
                                    <Radio value={1}><Trans>No, or negligible risk identified</Trans></Radio>
                                    <Radio value={2}><Trans>Identified risk is higher than negligible</Trans></Radio>
                                    <Radio value={3}><Trans>Did not perform risk assessment yet</Trans></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item name={["onboarding", "profile", "riskAssessmentDescription"]} style={{marginBottom: 10}}>
                            <TextArea rows={4} disabled={true} placeholder={i18n._(t`Please write the risk assesment here...`)}/>
                        </Form.Item>
                        <Form.Item name={["onboarding", "profile", "riskAssessmentDeadline"]} style={{marginBottom: 0}}>
                            <DatePicker disabled={true}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>4. If there is a risk to be mixed what countermeasures will be taken and provide the target date? (Finished products)</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "goodsMixingCounterMeasureDeadline"]}
                        >
                            <DatePicker disabled={true} style={{marginTop: 10}}/>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "goodsMixingCounterMeasure"]}
                        >
                            <TextArea rows={4} disabled={true} placeholder={i18n._(t`Please write the countermeasure here...`)}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>5. If there is a risk to be mixed what countermeasures will be taken and provide the target date? (Raw materials)</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "rawMatGoodsMixingCounterMeasureDeadline"]}
                        >
                            <DatePicker disabled={true} style={{marginTop: 10}}/>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "rawMatGoodsMixingCounterMeasure"]}
                        >
                            <TextArea rows={4} disabled={true} placeholder={i18n._(t`Please write the countermeasure here...`)}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>6. Have you identified or deteced the followings in the supply chain: eg. Unusual trade flows / volume mismatches or any suspicious indicates possible circumvention risk? If yes please describe.</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            name={["onboarding", "profile", "helpfulForIdentifyingCircumventionRisks"]}
                        >
                            <Radio.Group disabled={true}>
                                <Space direction="horizontal">
                                    <Radio value={1}><Trans>Yes</Trans></Radio>
                                    <Radio value={2}><Trans>No</Trans></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            style={{marginTop: 10}}
                            name={["onboarding", "profile", "helpfulForIdentifyingCircumventionRisksDescription"]}
                        >
                            <TextArea rows={4} disabled={true} placeholder={i18n._(t`Please write the description...`)}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>7. Do you have any concerns in relation to the collected and provided details?</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            name={["onboarding", "profile", "haveAnyConcerns"]}
                        >
                            <Radio.Group disabled={true}>
                                <Space direction="horizontal">
                                    <Radio value={1}><Trans>Yes</Trans></Radio>
                                    <Radio value={2}><Trans>No</Trans></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>8. Not all substantiated concerns or enforcement actions may be public. Do you have any, especially early-stage or ongoing investigations or any information that would point to a risk that the relevant products are non-compliant regarding EUDR?</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            name={["onboarding", "profile", "haveAnyEarlyStageInvestigation"]}
                        >
                            <Radio.Group disabled={true}>
                                <Space direction="horizontal">
                                    <Radio value={1}><Trans>Yes</Trans></Radio>
                                    <Radio value={2}><Trans>No</Trans></Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>9. Have any known risks, inspection findings, or alerts for your products, origins, or traders been published (e.g.: BMEL Germany, Nébih Hungary). Please write a few sentences about what it contains.</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "knownRisksForSpecificProducts"]}
                        >
                            <TextArea rows={4} disabled={true} placeholder={i18n._(t`What it contains...`)} style={{marginTop: 10}}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>10. If you have any certification, please provide it (upload) alongside with its identification number! Please write a few sentences about what it contains.</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            style={{marginBottom: 0, marginLeft: 0, paddingLeft: 0}}
                            name={["onboarding", "profile", "certification", "fileName"]}
                        >
                            <TextArea
                                style={{cursor: "pointer", resize: "none", paddingLeft:0, textDecoration: "underline"}}
                                readOnly
                                bordered={false} rows={0} onClick={() => handleDownloadFile(
                                form.getFieldValue(["onboarding", "profile", "certification", "filePath"]),
                                form.getFieldValue(["onboarding", "profile", "certification", "fileName"])
                            )}></TextArea>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "certId"]}>
                            <Input disabled={true} placeholder={i18n._(t`Certification ID`)} style={{marginTop: 10}}/>
                        </Form.Item>
                        <Form.Item
                            style={{marginBottom: 10}}
                            name={["onboarding", "profile", "certIdDescription"]}
                        >
                            <TextArea disabled={true} rows={4} placeholder={i18n._(t`Description....`)}/>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>11. Please provide the Certifications e.g. FSC, RSPO, or Fairtrade. (if any)</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            style={{marginBottom: 0, marginLeft: 0, paddingLeft: 0}}
                            name={["onboarding", "profile", "certEgFscOrRspoOrFairtrade", "fileName"]}
                        >
                            <TextArea
                                style={{cursor: "pointer", resize: "none", paddingLeft:0, textDecoration: "underline"}}
                                readOnly
                                bordered={false} rows={0} onClick={() => handleDownloadFile(
                                form.getFieldValue(["onboarding", "profile", "certEgFscOrRspoOrFairtrade", "filePath"]),
                                form.getFieldValue(["onboarding", "profile", "certEgFscOrRspoOrFairtrade", "fileName"])
                            )}></TextArea>
                        </Form.Item>
                    </Descriptions.Item>
                    <Descriptions.Item span={4} label={<Trans>12. Review Reports from Third-Party Verifiers</Trans>} labelStyle={{width: '50%'}}>
                        <Form.Item
                            style={{marginBottom: 0, marginLeft: 0, paddingLeft: 0}}
                            name={["onboarding", "profile", "reviewReportsFromThirdPartyVerifiers", "fileName"]}
                        >
                            <TextArea
                                style={{cursor: "pointer", resize: "none", paddingLeft:0, textDecoration: "underline"}}
                                readOnly
                                bordered={false} rows={0} onClick={() => handleDownloadFile(
                                form.getFieldValue(["onboarding", "profile", "reviewReportsFromThirdPartyVerifiers", "filePath"]),
                                form.getFieldValue(["onboarding", "profile", "reviewReportsFromThirdPartyVerifiers", "fileName"])
                            )}></TextArea>
                        </Form.Item>
                    </Descriptions.Item>
                </Descriptions>




                {!requiresSupplierSelection && (
                    <div style={{marginTop: 10}} className="center-card">
                        <Button
                            loading={loading}
                            htmlType="button"
                            type="primary"
                            onClick={() => reset()}
                        >
                            <ButtonWithIcon
                                icon={<FileSyncOutlined/>}
                                text={<Trans>I would like to re-enter the data.</Trans>}
                            />
                        </Button>
                    </div>
                )}
                    </Form>
                </Spin>
            </div>
        </>
    )
}
export default MyBusinessProfileScreen;
